import { useMemo } from 'react';
import { Box, Typography, Stack, Chip, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { detectOutliersIQR, detectOutliersZScore, filterByWindow } from '../statistics';
import { useTimeline } from '../TimelineContext';

export default function AnomalyWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow, seek } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKey = widgetConfig?.sensorKey || '';
  const method = widgetConfig?.method || 'iqr';

  const rec = recordings.find((r) => r.id === recId);

  const result = useMemo(() => {
    if (!rec?.data?.[sensorKey]) return null;
    let points = rec.data[sensorKey];
    if (timeWindow) points = filterByWindow(points, timeWindow.start, timeWindow.end);
    if (points.length < 4) return null;

    if (method === 'iqr') return { ...detectOutliersIQR(points), method: 'IQR' };
    return { ...detectOutliersZScore(points), method: 'Z-Score' };
  }, [rec, sensorKey, timeWindow, method]);

  const sensorName = friendlyNameConverter(sensorKey || '');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {result ? (
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
            <Chip label={`${result.outliers.length} outliers`} size="small" color={result.outliers.length > 0 ? 'error' : 'success'} variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            {result.lowerFence != null && <Chip label={`Fences: ${result.lowerFence.toFixed(1)} – ${result.upperFence.toFixed(1)}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
            {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
          </Stack>
          {result.outliers.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }}>Time</TableCell>
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.outliers.slice(0, 50).map((dp, i) => (
                  <TableRow key={i} hover sx={{ cursor: 'pointer' }} onClick={() => seek(dp.time)}>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>{new Date(dp.time).toLocaleTimeString()}</TableCell>
                    <TableCell sx={{ py: 0.25, fontSize: '0.7rem', color: 'error.main' }} align="right">{Number(dp.value).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {result.outliers.length > 50 && (
                  <TableRow><TableCell colSpan={2} sx={{ py: 0.25, fontSize: '0.7rem', textAlign: 'center' }}>...and {result.outliers.length - 50} more</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="success.main" sx={{ textAlign: 'center', mt: 2 }}>No outliers detected for {sensorName}</Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and sensor</Typography>
        </Box>
      )}
    </Box>
  );
}
